import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Input,
  FormGroup,
  Label,
  Alert,
  Badge,
  Row,
  Col,
  Spinner,
  Card,
  CardBody
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FaFileInvoice, FaTruck, FaSearch, FaCheckCircle, FaClock, FaMoneyBillWave } from 'react-icons/fa';
import { formatPrice } from '../../helpers/utility';
import './DocumentSelectionModal.css'; // Assuming you have some custom styles
const DocumentSelectionModal = ({
  isOpen,
  toggle,
  typeDocument, // 'FACTURE' or 'BON_LIVRAISON'
  documents,
  loading,
  selectedDocuments,
  onDocumentsSelected
}) => {
  const { t } = useTranslation();
  const [localSelectedDocs, setLocalSelectedDocs] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const isAvance = typeDocument === "AVANCE";

  useEffect(() => {
    setLocalSelectedDocs(selectedDocuments || []);
  }, [selectedDocuments]);

  useEffect(() => {
    const filteredDocs = getFilteredDocuments();
    // Only consider documents with remaining amount > 0 for the select all checkbox state
    const selectableDocs = filteredDocs.filter(doc =>
      (doc.montantTotalTTC - (doc.montantRegle || doc.montantPaye || 0)) > 0
    );

    const selectedSelectableDocs = localSelectedDocs.filter(selected =>
      selectableDocs.some(doc => doc.id === selected.id)
    );

    setSelectAll(
      selectedSelectableDocs.length > 0 &&
      selectableDocs.length > 0 &&
      selectedSelectableDocs.length === selectableDocs.length
    );
  }, [localSelectedDocs, documents, searchTerm]);

  const getFilteredDocuments = () => {
    if (!documents) return [];

    return documents.filter(doc => {
      const searchLower = searchTerm.toLowerCase();
      return (
        doc.numero?.toLowerCase().includes(searchLower) ||
        doc.reference?.toLowerCase().includes(searchLower) ||
        doc.clientNom?.toLowerCase().includes(searchLower)
      );
    });
  };

  const handleDocumentSelection = (document, isSelected) => {
    if (isSelected) {
      setLocalSelectedDocs(prev => [...prev, document]);
    } else {
      setLocalSelectedDocs(prev => prev.filter(doc => doc.id !== document.id));
    }
  };

  const handleSelectAll = () => {
    const filteredDocs = getFilteredDocuments();
    // Get only documents with remaining amount > 0
    const selectableDocs = filteredDocs.filter(doc =>
      (doc.montantTotalTTC - (doc.montantPaye || doc.montantRegle || 0)) > 0
    );

    if (selectAll) {
      // Deselect all filtered documents
      setLocalSelectedDocs(prev =>
        prev.filter(selected => !selectableDocs.some(filtered => filtered.id === selected.id))
      );
    } else {
      // Select all filtered documents that aren't already selected
      const newSelections = selectableDocs.filter(doc =>
        !localSelectedDocs.some(selected => selected.id === doc.id)
      );
      setLocalSelectedDocs(prev => [...prev, ...newSelections]);
    }
  };

  const calculateTotalAmount = () => {
    return localSelectedDocs.reduce((total, doc) => {
      return total + (doc.montantTotalTTC - (doc.montantRegle || doc.montantPaye || 0));
    }, 0);
  };

  const calculateStatistics = () => {
    const filteredDocs = getFilteredDocuments();
    return {
      total: filteredDocs.length,
      totalAmount: filteredDocs.reduce((sum, doc) => sum + (doc.montantTotalTTC), 0),
      selected: localSelectedDocs.length,
      selectedAmount: calculateTotalAmount(),
      unpaidAmount: filteredDocs.reduce((sum, doc) => sum + (doc.montantTotalTTC - (doc.montantRegle || doc.montantPaye)), 0)
    };
  };

  const handleConfirm = () => {
    onDocumentsSelected(localSelectedDocs);
    toggle();
  };

  const getStatusBadge = (document) => {
    if (typeDocument === 'FACTURE') {
      switch (document.statut) {
        case 'BROUILLON':
          return <Badge color="secondary">{t('facture.brouillon')}</Badge>;
        case 'VALIDEE':
          return <Badge color="primary">{t('facture.validee')}</Badge>;
        case 'PARTIELLEMENT_PAYEE':
          return <Badge color="warning">{t('facture.partiellementPayee')}</Badge>;
        case 'PAYEE':
          return <Badge color="success">{t('facture.payee')}</Badge>;
        case 'EN_RETARD':
          return <Badge color="danger">{t('facture.enRetard')}</Badge>;
        default:
          return <Badge color="secondary">{document.statut}</Badge>;
      }
    } else {
      // Bon de livraison
      switch (document.status) {
        case 'EN_PREPARATION':
          return <Badge color="warning">{t('delivery.enpreparation')}</Badge>;
        case 'EN_COURS':
          return <Badge color="secondary">{t('delivery.encours')}</Badge>;
        case 'LIVRE':
          return <Badge color="success">{t('delivery.livre')}</Badge>;
        case 'RETOUR':
          return <Badge color="danger">{t('delivery.retourne')}</Badge>;
        default:
          return <Badge color="secondary">{document.status}</Badge>;
      }
    }
  };

  const getPaymentStatusBadge = (document) => {
    const montantPaye = document.montantRegle || document.montantPaye || 0;
    const montantTotal = document.montantTotalTTC || 0;

    if (montantPaye === 0) {
      return <Badge color="danger">{t('reglement.impaye')}</Badge>;
    } else if (montantPaye < montantTotal) {
      return <Badge color="warning">{t('reglement.partiel')}</Badge>;
    } else {
      return <Badge color="success">{t('reglement.paye')}</Badge>;
    }
  };

  const statistics = calculateStatistics();
  const filteredDocuments = getFilteredDocuments();

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" className="document-selection-modal">
      <ModalHeader toggle={toggle}>
        <div className="d-flex align-items-center">
          {isAvance ? (
            <FaMoneyBillWave className="text-warning me-2" />
          ) : typeDocument === 'FACTURE' ? (
            <FaFileInvoice className="text-primary me-2" />
          ) : (
            <FaTruck className="text-success me-2" />
          )}

          {isAvance
            ? t('reglement.selectDocumentsAvance')
            : typeDocument === 'FACTURE'
              ? t('reglement.selectFactures')
              : t('reglement.selectBonLivraisons')}
        </div>
      </ModalHeader>
      <ModalBody>
        {/* Search and Controls */}
        <Row className="mb-3">
          <Col md={8}>
            <FormGroup>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <FaSearch />
                </span>
                <Input
                  type="text"
                  placeholder={t('reglement.searchDocuments')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </FormGroup>
          </Col>
          <Col md={4} className="d-flex align-items-end">
            <div className="ms-auto text-end">
              <small className="text-muted d-block">
                {filteredDocuments.length} {t('reglement.documentsFound')}
              </small>
              <small className="text-muted">
                {localSelectedDocs.length} {t('reglement.documentsSelected')}
              </small>
            </div>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <div className="mt-2 text-muted">{t('common.loading')}</div>
          </div>
        ) : (
          <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm">              <thead className="table-light sticky-top">
              <tr>
                <th width="50">
                  {!isAvance && (
                    <Input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      disabled={filteredDocuments.filter(doc =>
                        ((doc.montantTotalTTC || doc.montantTTC) -
                          (doc.montantPaye || doc.montantRegle || 0)) > 0
                      ).length === 0}
                    />
                  )}
                </th>
                <th>{t('reglement.numero')}</th>
                <th>{t('reglement.client')}</th>
                <th>{t('reglement.date')}</th>
                <th>{t('reglement.montantTTC')}</th>
                <th>{t('reglement.montantPaye')}</th>
                <th>{t('reglement.montantRestant')}</th>
                {typeDocument === 'FACTURE' && <th>{t('reglement.dateEcheance')}</th>}
                <th>{t('reglement.statut')}</th>
                <th>{t('reglement.paiement')}</th>
              </tr>
            </thead>
              <tbody>
                {filteredDocuments.length === 0 ? (
                  <tr>
                    <td colSpan={typeDocument === 'FACTURE' ? "10" : "9"} className="text-center text-muted py-4">
                      {searchTerm ? (
                        <>
                          <FaSearch className="mb-2" />
                          <div>{t('reglement.noDocumentsFound')}</div>
                        </>
                      ) : (
                        <>
                          {typeDocument === 'FACTURE' ? (
                            <FaFileInvoice className="mb-2" />
                          ) : (
                            <FaTruck className="mb-2" />
                          )}
                          <div>
                            {typeDocument === 'FACTURE'
                              ? t('reglement.noFacturesImpayees')
                              : t('reglement.noBonLivraisonsNonRegles')}
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredDocuments.map((document) => (
                    <tr key={document.id} className={localSelectedDocs.some(doc => doc.id === document.id) ? 'table-active' : ''}>                      <td>
                      {(document.montantTotalTTC - (document.montantPaye || document.montantRegle || 0)) > 0 ? (
                        <Input
                          type="checkbox"
                          checked={localSelectedDocs.some(doc => doc.id === document.id)}
                          onChange={(e) => handleDocumentSelection(document, e.target.checked)}
                        />
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                      <td>
                        <strong>{document.numero}</strong>
                      </td>
                      <td>
                        {document.clientNom || 'N/A'}
                      </td>
                      <td>
                        {document.createdAt || document.dateFacture}
                      </td>
                      <td>
                        <strong>{formatPrice(document.montantTotalTTC, false)} DH</strong>
                      </td>
                      <td>
                        {formatPrice(document.montantPaye || document.montantRegle || 0, false)} DH
                      </td>
                      <td className="fw-bold text-danger">
                        {formatPrice(

                          (document.montantTotalTTC - (document.montantPaye || document.montantRegle || 0)),
                          false
                        )} DH
                      </td>
                      {typeDocument === 'FACTURE' && (
                        <td>
                          {document.dateEcheance ?
                            new Date(document.dateEcheance).toLocaleDateString('fr-FR') :
                            '-'
                          }
                        </td>
                      )}
                      <td>
                        {getStatusBadge(document)}
                      </td>
                      <td>
                        {getPaymentStatusBadge(document)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}

        {/* Selected documents summary */}
        {localSelectedDocs.length > 0 && (
          <Alert color="success" className="mt-3">
            <Row>
              <Col md={6}>
                <div className="d-flex align-items-center">
                  <FaCheckCircle className="text-success me-2" />
                  <div>
                    <strong>{t('reglement.documentsSelectionnes')}:</strong> {localSelectedDocs.length}
                    <br />
                    <small className="text-muted">
                      {localSelectedDocs.map(doc => doc.numero).join(', ')}
                    </small>
                  </div>
                </div>
              </Col>
              <Col md={6} className="text-end">
                <div>
                  <strong>{t('reglement.montantTotal')}:</strong>
                  <div className="h5 text-success mb-0">
                    {formatPrice(calculateTotalAmount(), false)} DH
                  </div>
                </div>
              </Col>
            </Row>
          </Alert>
        )}
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between">
        <div className="d-flex align-items-center">
          <div className="me-4">
            <small className="text-muted d-block">
              {localSelectedDocs.length} {t('reglement.documentsSelected')}
            </small>
            <strong className="text-primary">
              {t('reglement.total')}: {formatPrice(calculateTotalAmount(), false)} DH
            </strong>
          </div>
        </div>
        <div>
          <Button color="secondary" onClick={toggle} className="me-2">
            {t('common.cancel')}
          </Button>
          <Button
            color="primary"
            onClick={handleConfirm}
            disabled={!isAvance && localSelectedDocs.length === 0}
          >
            <FaCheckCircle className="me-1" />
            {t('common.confirm')} ({localSelectedDocs.length})
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default DocumentSelectionModal;
